import React, {useState, useRef, useEffect, useCallback, useMemo} from 'react'
import {Form, Button, Spinner, Modal, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {uploadCanvasToS3} from '../../../../../services/s3Upload'
import {toast} from 'react-toastify'
import {CardData, ImageUploaderProps} from '../type'
import './ImageUploader.scss'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import Cropper from 'react-easy-crop'
import {ENV} from '../../../../Utils/_helpers/EnvHelper'

// Helper function to create an image from a file
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.src = url
  })

const getStatsCrop = (platform: 'apple' | 'google', cropWidth: number, uploadHeight: number) => {
  if (platform === 'google') {
    return {
      width: 240,
      height: 240,
    }
  }
  return {
    width: 300,
    height: 150,
  }
}

type PixelCrop = {
  x: number
  y: number
  width: number
  height: number
}

const calculateSmartCropSize = (
  imageWidth: number,
  imageHeight: number,
  targetWidth: number,
  targetHeight: number
): {width: number; height: number} => {
  const safeTargetWidth = targetWidth || imageWidth
  const safeTargetHeight = targetHeight || imageHeight

  const imageAspect = imageWidth / imageHeight
  const targetAspect = safeTargetWidth / safeTargetHeight

  if (!Number.isFinite(imageAspect) || !Number.isFinite(targetAspect)) {
    return {
      width: safeTargetWidth,
      height: safeTargetHeight,
    }
  }

  // Fit logo within target while maintaining aspect ratio
  if (imageAspect > targetAspect) {
    // Wider logo - fit to width
    return {
      width: safeTargetWidth,
      height: safeTargetWidth / imageAspect,
    }
  }

  // Taller logo - fit to height
  return {
    width: safeTargetHeight * imageAspect,
    height: safeTargetHeight,
  }
}

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: PixelCrop,
  isCircular: boolean = false,
  cropWidth: number,
  cropHeight: number,
  uploadWidth: number,
  uploadHeight: number
): Promise<Blob> => {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Failed to get canvas context')
  }

  // ---------- Accurate cropping ----------
  // 1) Draw the exact crop area onto a temporary canvas sized (cropWidth x cropHeight)
  const tempCanvas = document.createElement('canvas')
  const {width: fittedWidth, height: fittedHeight} = calculateSmartCropSize(
    pixelCrop.width,
    pixelCrop.height,
    cropWidth,
    cropHeight
  )
  tempCanvas.width = fittedWidth
  tempCanvas.height = fittedHeight
  const tempCtx = tempCanvas.getContext('2d')
  if (!tempCtx) throw new Error('Failed to get temp canvas context')

  // If circular crop – apply a clipping mask on temp canvas first
  if (isCircular) {
    tempCtx.beginPath()
    tempCtx.arc(cropWidth / 2, cropHeight / 2, cropWidth / 2, 0, 2 * Math.PI)
    tempCtx.clip()
  }

  // Draw the selected pixels from the original image into the temp canvas
  tempCtx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    fittedWidth,
    fittedHeight
  )

  // 2) Prepare the final canvas which may be larger (e.g., Google banner)
  const finalWidth = uploadWidth > 0 ? uploadWidth : cropWidth
  const finalHeight = uploadHeight > 0 ? uploadHeight : cropHeight
  canvas.width = finalWidth
  canvas.height = finalHeight

  // Fill with transparent background, then draw temp canvas centered
  const offsetX = (finalWidth - fittedWidth) / 2
  const offsetY = (finalHeight - fittedHeight) / 2
  ctx.drawImage(tempCanvas, offsetX, offsetY)

  // Return as blob with PNG format to support transparency
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else throw new Error('Canvas is empty')
      },
      'image/png',
      0.95
    ) // Changed to PNG for transparency support
  })
}

interface EnhancedImageUploaderProps extends ImageUploaderProps {
  disabled?: boolean
  shape?: 'rectangle' | 'circle'
  width?: number | string
  height?: number | string
  cropWidth?: number
  cropHeight?: number
  uploadWidth?: number
  uploadHeight?: number
  aspectRatio?: number
  isTemplate?: boolean
  tooltip?: string
  showBothCropper?: boolean
  cardData?: CardData
  allowFreeAspect?: boolean
}

const ImageUploader: React.FC<EnhancedImageUploaderProps> = ({
  isTemplate = false,
  tooltip,
  label,
  type,
  currentImage,
  onImageSelected,
  onUploadStart,
  onUploadEnd,
  disabled = false,
  shape = 'rectangle',
  width = '100%',
  height = 120,
  cropWidth = 320,
  cropHeight = 320,
  uploadWidth,
  uploadHeight,
  aspectRatio,
  showBothCropper = false,
  cardData,
  allowFreeAspect = false,
}) => {
  const [image, setImage] = useState<string | null>(currentImage || null)
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const {t} = useI18n()

  // Crop state
  const [crop, setCrop] = useState({x: 0, y: 0})
  const [zoom, setZoom] = useState(1)
  const [minZoom, setMinZoom] = useState(0.3)
  const [showCropModal, setShowCropModal] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(null)

  const [selectedPlatform, setSelectedPlatform] = useState<'apple' | 'google'>('apple')

  // Separate states for dual cropping
  const [appleCrop, setAppleCrop] = useState({x: 0, y: 0})
  const [googleCrop, setGoogleCrop] = useState({x: 0, y: 0})
  const [appleZoom, setAppleZoom] = useState(1)
  const [googleZoom, setGoogleZoom] = useState(1)
  const [appleMinZoom, setAppleMinZoom] = useState(0.3)
  const [googleMinZoom, setGoogleMinZoom] = useState(0.3)
  const [appleCroppedAreaPixels, setAppleCroppedAreaPixels] = useState<PixelCrop | null>(null)
  const [googleCroppedAreaPixels, setGoogleCroppedAreaPixels] = useState<PixelCrop | null>(null)
  const [appleImageSrc, setAppleImageSrc] = useState<string | null>(null)
  const [googleImageSrc, setGoogleImageSrc] = useState<string | null>(null)

  const [isAspectLocked, setIsAspectLocked] = useState<boolean>(true)

  // Memoize values that don't need to be recalculated on every render
  const isCircular = useMemo(() => {
    if (showBothCropper) {
      return selectedPlatform === 'google'
    }
    return shape === 'circle'
  }, [shape, showBothCropper, selectedPlatform])

  const cropAspect = useMemo(() => {
    if (showBothCropper) {
      // For logo cropping, Apple uses rectangle aspect, Google uses circle (1:1)
      return selectedPlatform === 'apple' ? cropWidth / cropHeight : 1
    }
    if (allowFreeAspect && !isAspectLocked) {
      return undefined
    }
    return aspectRatio || (isCircular ? 1 : cropWidth / cropHeight)
  }, [
    allowFreeAspect,
    aspectRatio,
    isCircular,
    cropWidth,
    cropHeight,
    showBothCropper,
    selectedPlatform,
    isAspectLocked,
  ])

  const containerClass = useMemo(
    () => (isCircular ? 'image-uploader' : 'image-uploader'),
    [isCircular]
  )

  const previewStyles = useMemo(
    () => ({
      width: width,
      height: height,
      borderRadius: '8px',
      backgroundColor: 'var(--bs-gray-100)',
    }),
    [width, height]
  )

  // Reset image state when currentImage changes
  useEffect(() => {
    setImage(currentImage || null)
  }, [currentImage])


  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      // Check file extension (only allow jpg, jpeg, png)
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      if (!fileExtension || !['jpg', 'jpeg', 'png'].includes(fileExtension)) {
        toast.error(
          t('S3.UPLOAD.TYPE_ERROR', {types: 'JPG, JPEG, PNG'}) ||
            t('WALLET_CARD.VALIDATION.CREATE.TYPE_ERROR')
        )
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = parseInt(ENV().REACT_APP_MAX_FILE_SIZE || '5242880')
      if (file.size > maxSize) {
        toast.error(t('S3.UPLOAD.SIZE_ERROR', {maxSize: maxSize / 1024 / 1024}))
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      // Store the selected file for later use
      setSelectedFile(file)

      // Create a preview of the image
      const reader = new FileReader()
      reader.onload = () => {
        if (showBothCropper) {
          // Only set the image for the selected platform
          if (selectedPlatform === 'apple') {
            setAppleImageSrc(reader.result as string)
            // Set Google image source to the same as Apple as placeholder
            if (!googleImageSrc) {
              setGoogleImageSrc(reader.result as string)
            }
          } else {
            setGoogleImageSrc(reader.result as string)
          }
        }
        setImageSrc(reader.result as string)
        // Show crop modal
        setShowCropModal(true)
      }
      reader.readAsDataURL(file)
    },
    [t, showBothCropper, selectedPlatform, googleImageSrc]
  )

  const onCropComplete = useCallback(
    (_croppedArea: unknown, cropPixels: PixelCrop) => {
      if (showBothCropper && selectedPlatform === 'apple') {
        setAppleCroppedAreaPixels(cropPixels)
      } else if (showBothCropper && selectedPlatform === 'google') {
        setGoogleCroppedAreaPixels(cropPixels)
      } else {
        setCroppedAreaPixels(cropPixels)
      }
    },
    [showBothCropper, selectedPlatform]
  )

  const handleCropCancel = useCallback(() => {
    setShowCropModal(false)
    setImageSrc(null)
    setSelectedFile(null)
    setSelectedPlatform('apple') // Reset platform selection
    // Reset dual cropping states
    setAppleImageSrc(null)
    setGoogleImageSrc(null)
    setAppleCrop({x: 0, y: 0})
    setGoogleCrop({x: 0, y: 0})
    setAppleZoom(1)
    setGoogleZoom(1)
    setAppleCroppedAreaPixels(null)
    setGoogleCroppedAreaPixels(null)
    // Reset file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const statApple = getStatsCrop('apple', cropWidth, cropHeight)
  const statGoogle = getStatsCrop('google', cropWidth, cropHeight)

  const handleCropConfirm = useCallback(async () => {
    try {
      setIsUploading(true)
      if (onUploadStart) onUploadStart()

      const nameApi = 'WALLET_CARD'
      const timestamp = new Date().getTime()
      const fileExtension = selectedFile?.name.split('.').pop()

      if (showBothCropper) {
        const appleFileName = `logo_apple_${timestamp}.${fileExtension}`
        const googleFileName = `logo_google_${timestamp}.${fileExtension}`

        // Use appropriate image source and cropped pixels for each platform
        const appleImgSrc = appleImageSrc || imageSrc
        const googleImgSrc = googleImageSrc || imageSrc
        const appleCropPixels = appleCroppedAreaPixels || croppedAreaPixels
        const googleCropPixels = googleCroppedAreaPixels || croppedAreaPixels

        if (!appleImgSrc || !appleCropPixels) {
          toast.error('Please upload and crop the Apple logo')
          return
        }

        // If Google is using Apple image as placeholder, use Apple crop pixels for Google too
        const finalGoogleCropPixels = googleCropPixels || appleCropPixels
        const finalGoogleImgSrc = googleImgSrc || appleImgSrc

        const appleBlob = await getCroppedImg(
          appleImgSrc,
          appleCropPixels,
          false,
          statApple.width,
          statApple.height,
          0,
          0
        )

        const googleBlob = await getCroppedImg(
          finalGoogleImgSrc,
          finalGoogleCropPixels,
          true,
          statGoogle.width,
          statGoogle.height,
          0,
          0
        )

        // Upload both versions
        const appleUrl = await uploadCanvasToS3(appleBlob, nameApi, appleFileName, t, 'apple')
        const googleUrl = await uploadCanvasToS3(googleBlob, nameApi, googleFileName, t, 'google')

        setImage(selectedPlatform === 'apple' ? appleUrl : googleUrl)

        if (onImageSelected) {
          onImageSelected(appleUrl, googleUrl)
        }
      } else {
        if (!imageSrc || !croppedAreaPixels || !selectedFile) return
        const prefix = isCircular ? 'profile' : 'wallet_card'
        const uniqueFileName = `${prefix}_${timestamp}.${fileExtension}`

        const croppedImage = await getCroppedImg(
          imageSrc,
          croppedAreaPixels,
          isCircular,
          cropWidth,
          cropHeight,
          uploadWidth || 0,
          uploadHeight || 0
        )

        const s3Url = await uploadCanvasToS3(croppedImage, nameApi, uniqueFileName, t, 'google')

        let originalImageUrl = ''

        if (isCircular) {
          const originalFileName = `profile_${timestamp}.${fileExtension}`
          const originalBlob = await getCroppedImg(
            imageSrc,
            croppedAreaPixels,
            isCircular,
            cropWidth,
            cropHeight,
            cropWidth,
            cropHeight
          )
          originalImageUrl = await uploadCanvasToS3(
            originalBlob,
            nameApi,
            originalFileName,
            t,
            'apple'
          )
        }

        setImage(s3Url)

        if (onImageSelected) {
          if (isCircular) {
            onImageSelected(originalImageUrl, s3Url)
          } else {
            onImageSelected(s3Url)
          }
        }
      }

      setShowCropModal(false)
      setImageSrc(null)
      setSelectedFile(null)
      setSelectedPlatform('apple')
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload image')
      console.error('Upload error:', error)
    } finally {
      setIsUploading(false)
      if (onUploadEnd) onUploadEnd()
    }
  }, [
    imageSrc,
    croppedAreaPixels,
    selectedFile,
    isCircular,
    cropWidth,
    cropHeight,
    uploadWidth,
    uploadHeight,
    onUploadStart,
    onImageSelected,
    onUploadEnd,
    t,
    showBothCropper,
    selectedPlatform,
    statApple,
    statGoogle,
    appleImageSrc,
    googleImageSrc,
    appleCroppedAreaPixels,
    googleCroppedAreaPixels,
  ])

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleRemoveImage = useCallback(() => {
    setImage(null)
    if (onImageSelected) {
      if (isCircular) {
        onImageSelected(null, null)
      } else {
        onImageSelected(null)
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [onImageSelected, isCircular])

  const handleChangeImage = useCallback(() => {
    if (image === cardData?.logoGoogle) {
      setImage(cardData?.logo || '')
    } else {
      setImage(cardData?.logoGoogle || '')
    }
  }, [image, cardData])

  // When the original image is loaded in the Cropper we will compute a zoom
  // that ensures the crop box is fully covered (no gray background edges).
  const handleMediaLoaded = useCallback(
    ({width: imgW, height: imgH}: {width: number; height: number}) => {
      if (showBothCropper) {
        if (selectedPlatform === 'apple') {
          const requiredZoom = Math.max(statApple.width / imgW, statApple.height / imgH)
          const zoomValue = Math.min(Math.max(requiredZoom, 0.1), 3)
          setAppleZoom(zoomValue)
          setAppleMinZoom(zoomValue)
        } else {
          if (allowFreeAspect) {
            // In free mode for Google logo, start with a gentle zoom
            // and allow the user to drag the image even if it does not fully cover the circle.
            setGoogleZoom(1)
            setGoogleMinZoom(0.1)
          } else {
            const requiredZoom = Math.max(statGoogle.width / imgW, statGoogle.height / imgH)
            const zoomValue = Math.min(Math.max(requiredZoom, 0.1), 3)
            setGoogleZoom(zoomValue)
            setGoogleMinZoom(zoomValue)
          }
        }
      } else {
        if (allowFreeAspect && !isAspectLocked) {
          // Free mode: user can freely drag and position image,
          // so do not force the image to completely cover the crop box.
          setZoom(1)
          setMinZoom(0.1)
        } else {
          // Locked aspect: keep current behavior, ensure the crop box is fully covered.
          const requiredZoom = Math.max(cropWidth / imgW, cropHeight / imgH)
          const zoomValue = Math.min(Math.max(requiredZoom, 0.1), 3)

          setZoom(zoomValue)
          setMinZoom(zoomValue)
        }
      }
    },
    [allowFreeAspect, cropWidth, cropHeight, isAspectLocked, showBothCropper, selectedPlatform, statApple, statGoogle]
  )

  return (
    <>
      <div className={`${containerClass}-container`}>
        <Form.Label className='text-center w-100 fw-semibold'>
          {label}
          {isTemplate && (
            <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip'>{tooltip}</Tooltip>}>
              <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1'></i>
            </OverlayTrigger>
          )}
        </Form.Label>
        <input
          type='file'
          accept='image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png'
          onChange={handleFileChange}
          style={{display: 'none'}}
          ref={fileInputRef}
          disabled={isUploading || disabled}
        />

        {!image ? (
          <Button
            variant='outline-secondary'
            onClick={handleButtonClick}
            className={`${containerClass}-button`}
            style={previewStyles}
            disabled={isUploading || disabled}
          >
            {isUploading ? (
              <>
                <Spinner animation='border' size='sm' className='mb-2' />
                <span className='text-muted'>{t('COMMON.UPLOADING')}</span>
              </>
            ) : (
              <>
                <div className='plus-icon mb-2'>+</div>
              </>
            )}
          </Button>
        ) : (
          <div className={`${containerClass}-preview`} style={previewStyles}>
            <img src={image} alt={image} className={`${containerClass}-preview-image`} />
            <Button
              variant='light'
              size='sm'
              className='remove-button'
              onClick={handleRemoveImage}
              disabled={isUploading || disabled}
              title={t('COMMON.CANCEL')}
            >
              <i className='bi bi-x-lg'></i>
            </Button>
            {showBothCropper && (
              <Button
                variant='light'
                size='sm'
                className='change-logo-button'
                onClick={handleChangeImage}
                disabled={isUploading || disabled}
                title={t('COMMON.CHANGE')}
              >
                <i className='bi bi-pencil-square'></i>
              </Button>
            )}
            {isUploading && (
              <div className='loading-overlay'>
                <Spinner animation='border' size='sm' />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Crop Modal */}
      <Modal
        show={showCropModal}
        onHide={handleCropCancel}
        centered
        size={showBothCropper ? 'xl' : 'lg'}
      >
        <Modal.Header closeButton>
          <Modal.Title className='text-start w-100'>
            {showBothCropper
              ? 'Set Wallet Logos'
              : isCircular
              ? t('WALLET_CARD.FORM.CROP_PROFILE_PICTURE')
              : t('IMAGE.CROP.TITLE')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showBothCropper ? (
            <div className='row'>
              {/* Apple Crop Section */}
              <div className='col-md-6'>
                <h6 className='text-center mb-3'>
                  <i className='bi bi-apple me-2'></i>
                  Apple Wallet Logo
                </h6>

                {/* Apple Image Upload */}
                <div className='text-center mb-3'>
                  <input
                    type='file'
                    accept='image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png'
                    onChange={(e) => {
                      setSelectedPlatform('apple')
                      handleFileChange(e)
                    }}
                    style={{display: 'none'}}
                    id='apple-file-input'
                    disabled={isUploading}
                  />
                  <Button
                    variant={appleImageSrc ? 'outline-secondary' : 'outline-primary'}
                    onClick={() => {
                      setSelectedPlatform('apple')
                      document.getElementById('apple-file-input')?.click()
                    }}
                    disabled={isUploading}
                  >
                    <i className='bi bi-upload me-2'></i>
                    {appleImageSrc ? 'Change Apple Logo' : 'Upload Apple Logo'}
                  </Button>
                </div>

                {/* Apple Cropper */}
                <div style={{position: 'relative', height: '250px', width: '100%'}}>
                  {appleImageSrc && (
                    <Cropper
                      image={appleImageSrc}
                      crop={appleCrop}
                      zoom={appleZoom}
                      aspect={statApple.width / statApple.height}
                      cropShape='rect'
                      showGrid={false}
                      cropSize={{
                        width: statApple.width,
                        height: statApple.height,
                      }}
                      minZoom={appleMinZoom}
                      restrictPosition={!allowFreeAspect}
                      onCropChange={setAppleCrop}
                      onCropComplete={(croppedArea, croppedAreaPixels) => {
                        setAppleCroppedAreaPixels(croppedAreaPixels)
                      }}
                      onMediaLoaded={(mediaData) => {
                        setSelectedPlatform('apple')
                        handleMediaLoaded(mediaData)
                      }}
                      onZoomChange={setAppleZoom}
                    />
                  )}
                </div>

                {/* Apple Zoom Control */}
                {appleImageSrc && (
                  <div className='mt-2'>
                    <Form.Label className='text-center d-block small'>
                      {t('WALLET_CARD.FORM.ZOOM')}
                    </Form.Label>
                    <Form.Range
                      value={appleZoom}
                      min={appleMinZoom}
                      max={3}
                      step={0.1}
                      onChange={(e) => {
                        const nextZoom = parseFloat(e.target.value)
                        setAppleZoom(nextZoom)
                      }}
                      className='w-100'
                    />
                    <div className='zoom-percent-control mt-2'>
                      <span className='zoom-percent-label'>{Math.round(appleZoom * 100)}%</span>
                      <Button
                        type='button'
                        className='zoom-percent-button ms-2'
                        onClick={() => {
                          const step = 0.01
                          const next = Math.max(appleZoom - step, appleMinZoom)
                          setAppleZoom(parseFloat(next.toFixed(2)))
                        }}
                      >
                        -
                      </Button>
                      <Button
                        type='button'
                        className='zoom-percent-button ms-2'
                        onClick={() => {
                          const step = 0.01
                          const next = Math.min(appleZoom + step, 3)
                          setAppleZoom(parseFloat(next.toFixed(2)))
                        }}
                      >
                        +
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Google Crop Section */}
              <div className='col-md-6'>
                <h6 className='text-center mb-3'>
                  <i className='bi bi-google me-2'></i>
                  Google Logo
                </h6>

                {/* Google Image Upload */}
                <div className='text-center mb-3'>
                  <input
                    type='file'
                    accept='image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png'
                    onChange={(e) => {
                      setSelectedPlatform('google')
                      handleFileChange(e)
                    }}
                    style={{display: 'none'}}
                    id='google-file-input'
                    disabled={isUploading}
                  />
                  <Button
                    variant={googleImageSrc ? 'outline-secondary' : 'outline-primary'}
                    onClick={() => {
                      setSelectedPlatform('google')
                      document.getElementById('google-file-input')?.click()
                    }}
                    disabled={isUploading}
                  >
                    <i className='bi bi-upload me-2'></i>
                    {googleImageSrc ? 'Change Google Logo' : 'Upload Google Logo'}
                  </Button>
                </div>

                {/* Google Cropper */}
                <div style={{position: 'relative', height: '250px', width: '100%'}}>
                  {googleImageSrc && (
                    <Cropper
                      image={googleImageSrc}
                      crop={googleCrop}
                      zoom={googleZoom}
                      aspect={1}
                      cropShape='round'
                      showGrid={false}
                      cropSize={{
                        width: statGoogle.width,
                        height: statGoogle.height,
                      }}
                      minZoom={googleMinZoom}
                      restrictPosition={!allowFreeAspect}
                      onCropChange={setGoogleCrop}
                      onCropComplete={(croppedArea, croppedAreaPixels) => {
                        setGoogleCroppedAreaPixels(croppedAreaPixels)
                      }}
                      onMediaLoaded={(mediaData) => {
                        setSelectedPlatform('google')
                        handleMediaLoaded(mediaData)
                      }}
                      onZoomChange={setGoogleZoom}
                    />
                  )}
                </div>

                {/* Google Zoom Control */}
                {googleImageSrc && (
                  <div className='mt-2'>
                    <Form.Label className='text-center d-block small'>
                      {t('WALLET_CARD.FORM.ZOOM')}
                    </Form.Label>
                    <Form.Range
                      value={googleZoom}
                      min={googleMinZoom}
                      max={3}
                      step={0.1}
                      onChange={(e) => {
                        const nextZoom = parseFloat(e.target.value)
                        setGoogleZoom(nextZoom)
                      }}
                      className='w-100'
                    />
                    <div className='zoom-percent-control mt-2'>
                      <span className='zoom-percent-label'>{Math.round(googleZoom * 100)}%</span>
                      <Button
                        type='button'
                        className='zoom-percent-button ms-2'
                        onClick={() => {
                          const step = 0.01
                          const next = Math.max(googleZoom - step, googleMinZoom)
                          setGoogleZoom(parseFloat(next.toFixed(2)))
                        }}
                      >
                        -
                      </Button>
                      <Button
                        type='button'
                        className='zoom-percent-button ms-2'
                        onClick={() => {
                          const step = 0.01
                          const next = Math.min(googleZoom + step, 3)
                          setGoogleZoom(parseFloat(next.toFixed(2)))
                        }}
                      >
                        +
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div style={{position: 'relative', height: '350px', width: '100%'}}>
                {imageSrc && (
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={cropAspect}
                    cropShape={isCircular ? 'round' : 'rect'}
                    showGrid={false}
                    cropSize={{
                      width: cropWidth,
                      height: cropHeight,
                    }}
                    minZoom={minZoom}
                    restrictPosition={!allowFreeAspect || isAspectLocked}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onMediaLoaded={handleMediaLoaded}
                    onZoomChange={setZoom}
                  />
                )}
              </div>
              <div className='d-flex justify-content-center align-items-center flex-column mt-3'>
                <p className='text-muted mb-2'>{t('WALLET_CARD.FORM.MOVE_IMAGE_HINT')}</p>

                <Form.Label>{t('WALLET_CARD.FORM.ZOOM')}</Form.Label>
                <Form.Range
                  value={zoom}
                  min={minZoom}
                  max={3}
                  step={0.1}
                  onChange={(e) => {
                    const nextZoom = parseFloat(e.target.value)
                    setZoom(nextZoom)
                  }}
                  className='w-75'
                />
                <div className='zoom-percent-control mt-2'>
                  <span className='zoom-percent-label'>{Math.round(zoom * 100)}%</span>
                  <Button
                    type='button'
                    className='zoom-percent-button ms-2'
                    onClick={() => {
                      const step = 0.01
                      const next = Math.max(zoom - step, minZoom)
                      setZoom(parseFloat(next.toFixed(2)))
                    }}
                  >
                    -
                  </Button>
                  <Button
                    type='button'
                    className='zoom-percent-button ms-2'
                    onClick={() => {
                      const step = 0.01
                      const next = Math.min(zoom + step, 3)
                      setZoom(parseFloat(next.toFixed(2)))
                    }}
                  >
                    +
                  </Button>
                </div>
                {allowFreeAspect && (
                  <div className='mt-3 text-center'>
                    <Form.Check
                      type='switch'
                      id='aspect-lock-toggle'
                      label='Lock aspect ratio to card'
                      checked={isAspectLocked}
                      onChange={() => setIsAspectLocked((locked) => !locked)}
                    />
                    <small className='text-muted d-block mt-1'>
                      Target logo area: {cropWidth} × {cropHeight}px
                    </small>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCropCancel} disabled={isUploading}>
            {t('COMMON.CANCEL')}
          </Button>
          <Button variant='primary' onClick={handleCropConfirm} disabled={isUploading}>
            {isUploading ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                {t('COMMON.UPLOADING')}
              </>
            ) : (
              t('IMAGE.CROP.CONFIRM')
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ImageUploader
